type QueueConfig = {
  maxSize?: number
  overflow?: boolean // only affects fixed length queues. If enabled, the oldest
  // element will be thrown out when adding a new element to
  // the full queue.
  // If disables, an error is thrown.
}

export default class Queue<T> {
  private _queue: T[] = []
  private readonly _config: QueueConfig
  get length(): number {
    return this._queue.length
  }

  /**
   * Creates a new queue.
   * The constructor can be called in one of the following ways:
   *   new Queue()
   *   new Queue(elements)
   *   new Queue({maxSize: 100, ...})
   *   new Queue(elements, {maxSize: 100, ...})
   *
   * @param elements The initial elements to add to the queue.
   * @param config The queue configuration.
   */
  constructor()
  constructor(config: QueueConfig)
  constructor(elements: T[], config?: QueueConfig)
  constructor(elementsOrConfig?: T[] | QueueConfig, config?: QueueConfig) {
    // check arguments
    let elements: T[] | undefined = undefined
    if (Array.isArray(elementsOrConfig)) elements = elementsOrConfig
    else config = elementsOrConfig

    // validate config
    if (config?.maxSize && config.maxSize < 1) {
      throw new Error('The maxSize setting must be greater than 1')
    }
    // write config
    this._config = {
      maxSize: config?.maxSize || undefined, // no maximum size set by default
      overflow: config?.overflow || false,
    }

    if (elements && elements.length > 0) {
      this.enqueue(...elements)
    }
  }

  /**
   * Add elements to the end of the queue.
   * @param elements
   */
  enqueue(...elements: T[]): void {
    if (this._config.maxSize) {
      // make sure that we do not violate the maximum queue size
      if (!this._config.overflow) {
        // overflow is disabled
        if (this._queue.length + elements.length > this._config.maxSize) {
          // adding the elements would result in overflow, but overflow is disabled
          // -> fail by throwing an error
          throw new Error(
            'Received to many elements to add to queue. The queue is configured ' +
              `to only store ${this._config.maxSize} elements. Either increase or ` +
              'disable the maxSize of the queue, or enable overflow to resolve this error.'
          )
        } else {
          // enqueue elements
          this._queue.push(...elements)
        }
      }
      if (this._config.overflow) {
        // overflow is enabled
        if (this._queue.length + elements.length > this._config.maxSize) {
          // adding the new elements results in overflow -> remove as many elements
          // from the start of the queue as required to fit the new elements.
          this._queue.splice(0, elements.length)
          this._queue.push(...elements)
        } else {
          // no overflow -> simply add all elements to the queue
          this._queue.push(...elements)
        }
      }
    } else {
      // there is no maximum queue size specified -> just add the elements
      this._queue.push(...elements)
    }
  }

  /**
   * Removes elements from the start of the queue and returns them.
   * @param numberOfElements The number of elements to take out of the queue.
   */
  dequeue(): T
  dequeue(numberOfElements = 1): T | T[] {
    const dequeued = this._queue.splice(0, numberOfElements)

    // return either a single element or a list of elements
    if (dequeued.length === 1) return dequeued[0]
    return dequeued
  }

  /**
   * Read access to the queue: returns a copy of the object at the specified
   * queue position.
   * @param position The queue position to return.
   */
  get(position: number): T {
    if (position > this.length - 1 || position < 0) {
      throw new Error(
        `Queue position ${position} is out of range. Make sure to specify a position ` +
          "between 0 and the queue's length."
      )
    }
    return { ...this._queue[position] }
  }

  /**
   * Read access to the queue: returns a copy of the queue.
   */
  getAll(): T[] {
    return [...this._queue]
  }
}
